import { useState, useCallback, useEffect } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import appConfig from "@/config/app-config";
import Button from "@/components/Button";
import BankIdLogo from "@/assets/icons/BankID_logo.svg?react";
import BankIdLogoWhite from "@/assets/icons/BankID_logo_white.svg?react";
import Chevron from "@/assets/icons/chevron.svg?react";
import { useAuth } from "@/hooks/useAuth";
import useVisibilityChange from "@/hooks/useVisibilityChange";
import Visible from "@/components/Visible";
import Spinner from "@/components/Spinner";
import Platform from "@/components/Platform";
import styles from "./Pages.module.scss";
import { useOnboarding } from "@/hooks/useOnboarding";
import Container from "@/components/Container";
import { getFlowConfig } from "@/data/flows";

type Props = {
  variant?: "A" | "B";
};

export default function Recommendation({ variant }: Props) {
  const { loginState, setLoginState } = useAuth();
  const { questionnaireCompleted, currentOnboardingFlow } = useOnboarding();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const flowParam = searchParams.get("flow");

  if (!questionnaireCompleted) {
    const flowConfig = flowParam ? getFlowConfig(flowParam) : undefined;
    return <Navigate to={flowConfig?.landingPath || "/"} replace />;
  }

  return (
    <Container>
      {loginState === "idle" && <PreSignupInfo variant={variant} />}
      {loginState !== "idle" && (
        <LoginFlow
          onSuccess={() => {
            navigate(`/ladda-ner?flow=${currentOnboardingFlow}`);
            setLoginState("idle");
          }}
          onConflict={() => {
            navigate("/redan-registrerad");
            setLoginState("idle");
          }}
        />
      )}
    </Container>
  );
}

const Login = () => {
  const { setLoginState, loginOnDevice, beginQrLoginFlow } = useAuth();
  const { currentOnboardingFlow, getAnswerRequestObject } = useOnboarding();

  const flowConfig = getFlowConfig(currentOnboardingFlow);

  const submission: WebOnboardingRegistrationSubmission = {
    treatmentArea: flowConfig?.treatmentArea || "OBESITY",
    answers: getAnswerRequestObject(),
    baseRedirectUrl: window.location.origin,
  };

  return (
    <div className="stack-1">
      <Platform mobile>
        <Button
          onClick={() => loginOnDevice(submission)}
          iconLeft={<BankIdLogoWhite width={22} />}
          iconRight={<Chevron />}
          fullWidth
        >
          BankID på den här enheten
        </Button>
        <Button
          onClick={() => beginQrLoginFlow(submission)}
          variant="tertiary"
          iconLeft={<BankIdLogo width={22} />}
          iconRight={<Chevron />}
          fullWidth
        >
          BankID på annan enhet
        </Button>
      </Platform>
      <Platform desktop>
        <div className="text-center">
          <Button
            onClick={() => beginQrLoginFlow(submission)}
            iconLeft={<BankIdLogoWhite width={22} />}
            iconRight={<Chevron />}
          >
            Logga in med Mobilt BankID
          </Button>
        </div>
      </Platform>
      <Visible when={appConfig.devLoginEnabled}>
        <div className="text-center">
          <Button
            variant="secondary"
            onClick={() => setLoginState("enter_identifier")}
          >
            Logga in med personnummer (DEV)
          </Button>
        </div>

        <p className={styles.noCommitment}>
          Ingen bindningstid — avsluta när som helst
        </p>
      </Visible>

      <div className={styles.disclaimer}>
        Genom att fortsätta accepterar du Blodtrycksdoktorns{" "}
        <a
          href="https://blodtrycksdoktorn.se/anvandarvillkor/"
          target="_blank"
          rel="noreferrer"
        >
          användarvillkor
        </a>{" "}
        och{" "}
        <a
          href="https://blodtrycksdoktorn.se/integritetspolicy/"
          target="_blank"
          rel="noreferrer"
        >
          integritetspolicy
        </a>
        .
      </div>
    </div>
  );
};

const PreSignupInfo = ({ variant }: Props) => {
  const { currentOnboardingFlow } = useOnboarding();
  const flowConfig = getFlowConfig(currentOnboardingFlow);

  if (!flowConfig) return null;

  const { RecommendationContent } = flowConfig;

  return (
    <div className="recommendation">
      <RecommendationContent variant={variant}>
        <Login />
      </RecommendationContent>
    </div>
  );
};

type LoginFlowProps = {
  onSuccess: () => void;
  onConflict: () => void;
};

const LoginFlow = ({ onSuccess, onConflict }: LoginFlowProps) => {
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const {
    loginWithCredentials,
    completeLogin,
    loginState,
    setLoginState,
    friendlyError,
    dismissFriendlyError,
  } = useAuth();

  const completeLoginWithStoredSessionId = useCallback(() => {
    const storedSessionId = localStorage.getItem("sessionId");

    if (storedSessionId) {
      console.log("Found a stored session, completing login");
      completeLogin(storedSessionId)
        .then(onSuccess)
        .catch((error: Error) => {
          if (error.message === "Conflict") {
            onConflict();
          }
        });
    }
  }, [completeLogin, onSuccess]);

  useVisibilityChange(completeLoginWithStoredSessionId);

  const loginDev = async (e: { preventDefault: () => void }) => {
    e.preventDefault();

    try {
      await loginWithCredentials(identifier);
      onSuccess();
    } catch (error: any) {
      if (error.message === "Conflict") {
        onConflict();
      }
      console.log(e);
    }
  };

  return (
    <div className={styles.pageContent}>
      <Visible when={loginState === "enter_identifier"}>
        <form onSubmit={loginDev} className="stack-1">
          <div>
            <label>
              Personnummer
              <input
                autoFocus
                type="text"
                id="auth-input"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                required
                autoComplete="off"
              />
            </label>
          </div>
          <Button type="submit">Logga in</Button>
        </form>
        <Button variant="text" onClick={() => setLoginState("idle")}>
          Avbryt
        </Button>
      </Visible>

      <Visible
        when={[
          "waiting_for_bankid",
          "starting",
          "polling",
          "user_signing",
          "authenticated",
        ].includes(loginState)}
      >
        <BankIdPrompt onSuccess={onSuccess} />
      </Visible>

      <Visible when={loginState === "error"}>
        {friendlyError ? (
          <>
            <h1>{friendlyError.friendlyTitle}</h1>
            <p>{friendlyError.friendlyMessage}</p>
            <p className="text-center">
              <Button
                onClick={() => {
                  dismissFriendlyError();
                  navigate("/", { replace: true });
                }}
              >
                Okej
              </Button>
            </p>
          </>
        ) : (
          <div className="text-center">
            <div className={styles.error}>Något gick fel.</div>
            <Button variant="secondary" onClick={() => setLoginState("idle")}>
              Försök igen
            </Button>
          </div>
        )}
      </Visible>
    </div>
  );
};

const BankIdPrompt = ({ onSuccess }: Pick<LoginFlowProps, "onSuccess">) => {
  const { loginState, activeQrCode, cancelLogin } = useAuth();

  useEffect(() => {
    if (loginState === "authenticated") {
      onSuccess();
    }
  }, [loginState, onSuccess]);

  return (
    <>
      <Visible when={loginState === "waiting_for_bankid"}>
        <div className="text-center stack-2">
          <div>
            <Spinner size="large" />
          </div>
          <div>
            BankID appen kommer att startas automatiskt om den är installerad på
            den här enheten.
          </div>
          <div>
            <Button variant="text" onClick={cancelLogin}>
              Avbryt inloggning
            </Button>
          </div>
        </div>
      </Visible>

      <Visible when={["starting", "polling"].includes(loginState)}>
        <div className="text-center stack-2 mt-40">
          {activeQrCode ? (
            <div className="flex justify-center">
              <div
                className={styles.qrWrapper}
                dangerouslySetInnerHTML={{
                  __html: activeQrCode,
                }}
              />
            </div>
          ) : (
            <div>
              <Spinner size="large" />
            </div>
          )}
          <div>
            Öppna appen Mobilt BankID på din mobiltelefon och tryck på
            QR-symbolen. Följ instruktionerna i appen.
          </div>
          <Button variant="text" onClick={cancelLogin}>
            Avbryt inloggning
          </Button>
        </div>
      </Visible>
      <Visible when={["user_signing", "authenticated"].includes(loginState)}>
        <div>
          Skriv in din säkerhetskod i BankID-appen och välj{" "}
          <strong>Identifiera</strong>.
        </div>
      </Visible>
    </>
  );
};
